/**
 * Utility to process image URLs.
 * Handles:
 * 1. Absolute URLs (http/https/blob/data) -> Returns as is.
 * 2. Relative paths starting with /api/uploads -> Prepends backend URL.
 * 3. Filenames -> Prepends image base URL.
 * 
 * @param {string} path - The image path or URL
 * @returns {string} - The full image URL
 */
export const getImageUrl = (path) => {
    if (!path) return "";

    // Handle new Product schema where images are objects { url: String }
    if (typeof path === 'object' && path !== null) {
        path = path.url || "";
        if (!path) return "";
    }

    // Check if it's a blob URL or Data URI (local preview)
    if (path.startsWith('blob:') || path.startsWith('data:')) return path;

    const envUrl = process.env.REACT_APP_BACKEND_URL;
    const backendUrl = (envUrl && envUrl.trim() !== "") ? envUrl : '';

    // Debug log to identify why images are failing (remove after fix)
    // console.log(`[getImageUrl] Path: ${path} | Backend: ${backendUrl}`);

    // Fix double /api/api if present in string
    if (typeof path === 'string') {
        path = path.replace(/\/api\/api\//g, '/api/');
    }

    // When running locally on localhost, map remote dev DB upload URLs to local backend
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
        if (typeof path === 'string' && (path.includes('v2-api.edisplayroom.com/api/uploads/') || path.includes('localhost:8100/api/uploads/'))) {
            const uploadsMatch = path.match(/\/api\/uploads\/(.+)$/);
            if (uploadsMatch) {
                return `${backendUrl}/api/uploads/${uploadsMatch[1]}`;
            }
        }
    }

    // Fix legacy absolute URLs containing localhost (from dev database)
    if (path.includes('localhost:8100')) {
        // Find everything after /api/uploads
        const uploadsMatch = path.match(/\/api\/uploads\/(.+)$/);
        if (uploadsMatch) {
            return `${backendUrl}/api/uploads/${uploadsMatch[1]}`;
        }
        // Fallback: search for filename after the last slash
        const parts = path.split('/');
        const filename = parts[parts.length - 1];
        return `${backendUrl}/api/uploads/${filename}`;
    }

    // Check if it's already an absolute URL (non-localhost)
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }

    // Handle relative paths
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;

    if (cleanPath.startsWith('api/uploads/')) {
        return `${backendUrl}/${cleanPath}`;
    }

    if (cleanPath.startsWith('uploads/')) {
        const filename = cleanPath.replace('uploads/', '');
        return `${backendUrl}/api/uploads/${filename}`;
    }

    if (cleanPath.startsWith('it-website/')) {
        return `${backendUrl}/${cleanPath}`;
    }

    // Otherwise treat as filename and append to default uploads path
    return `${backendUrl}/api/uploads/${cleanPath}`;
};
